import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import OptionsPanel from '../components/OptionsPanel';
import Kozijnen from '../components/Kozijnen';
import Summary from '../components/Summary';
import ConfiguratorShell from '../components/ConfiguratorShell';
import { useTranslation } from '../context/TranslationContext';
import {
  applyPartBandSectionCount,
  applyPartBandSectionWindowType,
  applyPartBandSectionWidth,
  resizePerPartBandSectionConfig,
} from '../lib/partSectionConfig';
import {
  applyPartWidthChange,
  ensurePartWidthsSum,
  MIN_PART_WIDTH_MM,
} from '../lib/partWidthDistribution';
import {
  trackFrameColorChange,
  KOZIJNEN_COLOR_FIELDS,
} from '../lib/configuratorColors';
import { DEFAULT_INTERIOR_WHITE_ID } from '../data/ralColors';
import {
  applyPersistedConfiguratorPreferences,
  trackSharedConfiguratorChange,
} from '../lib/configuratorGlazing';
import { useConfiguratorDirtyState } from '../hooks/useConfiguratorDirtyState';
import { useConfiguratorLoadState } from '../hooks/useConfiguratorLoadState';
import {
  createDefaultAdditionalItems,
  withDefaultAdditionalItems,
  applyAdditionalItemsChange,
} from '../lib/additionalItems';
import {
  windowTypes,
  profileSystems,
  glassTextures,
  glazingTypes,
  colors,
  openingTypes,
  hardware,
  accessories,
  DIMENSION_CONSTRAINTS,
  partsOptions,
  overheadOptions,
  woodJointOptions,
  getColorById,
  divisionTypes,
  raamtypes,
  PRICING
} from '../data/mockData';

const DEFAULT_CONFIG = {
  width: 1500,
  height: 1500,
  profileSystem: 'schuco',
  glassTexture: 'helder-glas',
  glazingType: 'double',
  color: 'anthracite',
  interiorFrameColor: 'ral-9016',
  openingType: 'none',
  hardware: [],
  accessories: [],
  parts: 1,
  quantity: 1,
  overhead: 'no-overhead',
  overheadParts: 1,
  overheadHeight: 300,
  underlight: 'no-underlight',
  underlightParts: 1,
  underlightHeight: 400,
  woodJoint: 'joint-45',
  divisionType: 'equal',
  raamtype: 'fixed-sash',
  rampDorpel: 'no-ramp-dorpel',
  vensterbank: 'no-vensterbank',
  partWindowTypes: ['fixed-sash'],
  partWidths: [1500],
  overheadPartWindowTypes: ['fixed-sash'],
  overheadPartWidths: [1500],
  underlightPartWindowTypes: ['fixed-sash'],
  underlightPartWidths: [1500],
  panelColors: [],
  panelOverheadColors: [],
  panelUnderlightColors: [],
  panelLineTypes: [],
  panelOverheadLineTypes: [],
  panelUnderlightLineTypes: [],
  ventilatorParts: [false],
  ventilatorOverheadParts: [false],
  ventilatorUnderlightParts: [false],
  overheadPerPart: [false],
  underlightPerPart: [false],
  partOverheadWindowTypes: ['fixed-sash'],
  partUnderlightWindowTypes: ['fixed-sash'],
  partOverheadSections: [1],
  partUnderlightSections: [1],
  partOverheadSectionWindowTypes: [['fixed-sash']],
  partUnderlightSectionWindowTypes: [['fixed-sash']],
  partOverheadSectionWidths: [null],
  partUnderlightSectionWidths: [null],
  widthManuallySet: false,
};

const Configurator = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const drawingBoardRef = useRef(null);
  const saveCurrentRef = useRef(null);

  const buildDefaultConfig = () =>
    withDefaultAdditionalItems(
      applyPersistedConfiguratorPreferences(DEFAULT_CONFIG, KOZIJNEN_COLOR_FIELDS)
    );

  const { config, setConfig } = useConfiguratorLoadState(location, buildDefaultConfig, (loaded) =>
    withDefaultAdditionalItems(loaded)
  );
  const { isDirty, markClean, markDirty, resetBaseline } = useConfiguratorDirtyState(config);
  
  // Track the current quantity input value from Summary component
  // Initialize to config.quantity (which defaults to 1)
  const [currentQuantityInput, setCurrentQuantityInput] = useState(config.quantity ?? 1);
  
  // Sync currentQuantityInput when config.quantity changes
  useEffect(() => {
    setCurrentQuantityInput(config.quantity ?? 1);
  }, [config.quantity]);
  
  const handleConfigChange = (field, value) => {
    trackFrameColorChange(field, value, KOZIJNEN_COLOR_FIELDS);
    trackSharedConfiguratorChange(field, value);
    markDirty();
    setConfig(prev => {
      const newConfig = { ...prev, [field]: value };
      
      // If user manually edits width, remember so we don't auto-adjust on parts change
      if (field === 'width') {
        newConfig.widthManuallySet = true;

        // Rescale existing part widths proportionally to match new total width
        const currentWidths = prev.partWidths && prev.partWidths.length
          ? prev.partWidths
          : [prev.width || value];
        const totalBefore = currentWidths.reduce((sum, w) => sum + w, 0) || 1;
        const scaleFactor = value / totalBefore;
        let scaled = currentWidths.map(w => Math.round(w * scaleFactor));
        // Adjust last segment so the sum matches exactly
        const sumAfter = scaled.reduce((sum, w) => sum + w, 0);
        const diff = value - sumAfter;
        scaled[scaled.length - 1] += diff;
        newConfig.partWidths = scaled;

        // Also rescale overhead part widths proportionally to match new total width
        if (prev.overhead === 'overhead') {
          const overheadParts = prev.overheadParts || 1;
          const currentOverheadWidths = prev.overheadPartWidths && prev.overheadPartWidths.length === overheadParts
            ? prev.overheadPartWidths
            : Array(overheadParts).fill(Math.floor((prev.width || value) / overheadParts));
          const overheadTotalBefore = currentOverheadWidths.reduce((sum, w) => sum + w, 0) || 1;
          const overheadScaleFactor = value / overheadTotalBefore;
          let overheadScaled = currentOverheadWidths.map(w => Math.round(w * overheadScaleFactor));
          // Adjust last segment so the sum matches exactly
          const overheadSumAfter = overheadScaled.reduce((sum, w) => sum + w, 0);
          const overheadDiff = value - overheadSumAfter;
          overheadScaled[overheadParts - 1] += overheadDiff;
          newConfig.overheadPartWidths = overheadScaled;
        }

        // Also rescale underlight part widths proportionally to match new total width
        if (prev.underlight === 'underlight') {
          const underlightParts = prev.underlightParts || 1;
          const currentUnderlightWidths = prev.underlightPartWidths && prev.underlightPartWidths.length === underlightParts
            ? prev.underlightPartWidths
            : Array(underlightParts).fill(Math.floor((prev.width || value) / underlightParts));
          const underlightTotalBefore = currentUnderlightWidths.reduce((sum, w) => sum + w, 0) || 1;
          const underlightScaleFactor = value / underlightTotalBefore;
          let underlightScaled = currentUnderlightWidths.map(w => Math.round(w * underlightScaleFactor));
          // Adjust last segment so the sum matches exactly
          const underlightSumAfter = underlightScaled.reduce((sum, w) => sum + w, 0);
          const underlightDiff = value - underlightSumAfter;
          underlightScaled[underlightParts - 1] += underlightDiff;
          newConfig.underlightPartWidths = underlightScaled;
        }
      }
      
      // When parts change, adjust the partWindowTypes array
      if (field === 'parts') {
        const currentParts = prev.parts || 1;
        const newParts = value;
        const currentTypes = prev.partWindowTypes || ['fixed-glass'];
        
        if (newParts > currentParts) {
          // Add more parts - duplicate the last window type
          const lastType = currentTypes[currentTypes.length - 1] || 'fixed-sash';
          newConfig.partWindowTypes = [...currentTypes, ...Array(newParts - currentParts).fill(lastType)];
        } else if (newParts < currentParts) {
          // Remove parts - keep only the first N
          newConfig.partWindowTypes = currentTypes.slice(0, newParts);
        }

        // Recalculate per-part widths for the new part count
        const targetWidth = prev.width || DIMENSION_CONSTRAINTS.MIN_WIDTH;
        const baseWidth = Math.floor(targetWidth / newParts);
        const partWidths = Array(newParts).fill(baseWidth);
        // Distribute any remainder to the last part so sum matches total width
        const remainder = targetWidth - baseWidth * newParts;
        partWidths[newParts - 1] += remainder;
        newConfig.partWidths = partWidths;
        
        // Initialize partWindowTypes with fixed-sash if not already set
        if (!newConfig.partWindowTypes || newConfig.partWindowTypes.length !== newParts) {
          newConfig.partWindowTypes = Array(newParts).fill('fixed-sash');
        }
        
        // Initialize panel colors and line types arrays to match new parts count
        const currentPanelColors = prev.panelColors || [];
        const currentPanelLineTypes = prev.panelLineTypes || [];
        if (newParts > currentPanelColors.length) {
          newConfig.panelColors = [...currentPanelColors, ...Array(newParts - currentPanelColors.length).fill(null)];
        } else {
          newConfig.panelColors = currentPanelColors.slice(0, newParts);
        }
        if (newParts > currentPanelLineTypes.length) {
          newConfig.panelLineTypes = [...currentPanelLineTypes, ...Array(newParts - currentPanelLineTypes.length).fill('none')];
        } else {
          newConfig.panelLineTypes = currentPanelLineTypes.slice(0, newParts);
        }
        const currentVentilator = prev.ventilatorParts || [];
        newConfig.ventilatorParts = [...currentVentilator.slice(0, newParts), ...Array(Math.max(0, newParts - currentVentilator.length)).fill(false)];

        // Resize overheadPerPart to match new parts count so per-part overhead divider draws correctly
        const currentOverheadPerPart = prev.overheadPerPart || [false];
        newConfig.overheadPerPart = Array.from({ length: newParts }, (_, i) => currentOverheadPerPart[i] ?? false);

        const currentUnderlightPerPart = prev.underlightPerPart || [false];
        newConfig.underlightPerPart = Array.from({ length: newParts }, (_, i) => currentUnderlightPerPart[i] ?? false);

        const currentPartUnderlightTypes = prev.partUnderlightWindowTypes || ['fixed-sash'];
        newConfig.partUnderlightWindowTypes = Array.from(
          { length: newParts },
          (_, i) => currentPartUnderlightTypes[i] ?? 'fixed-sash'
        );

        const currentPartOverheadTypes = prev.partOverheadWindowTypes || ['fixed-sash'];
        newConfig.partOverheadWindowTypes = Array.from(
          { length: newParts },
          (_, i) => currentPartOverheadTypes[i] ?? 'fixed-sash'
        );

        // Per-part overhead/underlight Vast Panel color & lines (indexed by main part)
        const neededBandLen = Math.max(newParts, prev.overheadParts || 1, prev.underlightParts || 1);
        const currentOhColors = prev.panelOverheadColors || [];
        const currentOhLines = prev.panelOverheadLineTypes || [];
        newConfig.panelOverheadColors = Array.from(
          { length: neededBandLen },
          (_, i) => currentOhColors[i] ?? null
        );
        newConfig.panelOverheadLineTypes = Array.from(
          { length: neededBandLen },
          (_, i) => currentOhLines[i] ?? 'none'
        );
        const currentUlColors = prev.panelUnderlightColors || [];
        const currentUlLines = prev.panelUnderlightLineTypes || [];
        newConfig.panelUnderlightColors = Array.from(
          { length: neededBandLen },
          (_, i) => currentUlColors[i] ?? null
        );
        newConfig.panelUnderlightLineTypes = Array.from(
          { length: neededBandLen },
          (_, i) => currentUlLines[i] ?? 'none'
        );

        Object.assign(newConfig, resizePerPartBandSectionConfig(prev, newParts));

        // Auto-adjust width based on number of parts IF the user hasn't overridden width
        if (!prev.widthManuallySet) {
          const suggestedWidth = Math.min(
            DIMENSION_CONSTRAINTS.MAX_WIDTH,
            Math.max(DIMENSION_CONSTRAINTS.MIN_WIDTH, newParts * 1000)
          );
          newConfig.width = suggestedWidth;

          // Update partWidths again to reflect the new total width
          const baseWidth2 = Math.floor(suggestedWidth / newParts);
          const partWidths2 = Array(newParts).fill(baseWidth2);
          const remainder2 = suggestedWidth - baseWidth2 * newParts;
          partWidths2[newParts - 1] += remainder2;
          newConfig.partWidths = partWidths2;
        }
      }
      
      // When overhead parts change, adjust the overheadPartWindowTypes array
      if (field === 'overheadParts') {
        const currentOverheadParts = prev.overheadParts || 1;
        const newOverheadParts = value;
        const currentTypes = prev.overheadPartWindowTypes || ['fixed-sash'];
        
        if (newOverheadParts > currentOverheadParts) {
          // Add more parts - duplicate the last window type
          const lastType = currentTypes[currentTypes.length - 1] || 'fixed-sash';
          newConfig.overheadPartWindowTypes = [...currentTypes, ...Array(newOverheadParts - currentOverheadParts).fill(lastType)];
        } else if (newOverheadParts < currentOverheadParts) {
          // Remove parts - keep only the first N
          newConfig.overheadPartWindowTypes = currentTypes.slice(0, newOverheadParts);
        }

        // Recalculate per-overhead-part widths for the new part count
        const targetWidth = prev.width || DIMENSION_CONSTRAINTS.MIN_WIDTH;
        const baseWidth = Math.floor(targetWidth / newOverheadParts);
        const overheadPartWidths = Array(newOverheadParts).fill(baseWidth);
        // Distribute any remainder to the last part so sum matches total width
        const remainder = targetWidth - baseWidth * newOverheadParts;
        overheadPartWidths[newOverheadParts - 1] += remainder;
        newConfig.overheadPartWidths = overheadPartWidths;
        
        // Initialize overheadPartWindowTypes with fixed-sash if not already set
        if (!newConfig.overheadPartWindowTypes || newConfig.overheadPartWindowTypes.length !== newOverheadParts) {
          newConfig.overheadPartWindowTypes = Array(newOverheadParts).fill('fixed-sash');
        }
        
        // Initialize overhead panel colors and line types arrays to match new overhead parts count
        const currentOverheadPanelColors = prev.panelOverheadColors || [];
        const currentOverheadPanelLineTypes = prev.panelOverheadLineTypes || [];
        if (newOverheadParts > currentOverheadPanelColors.length) {
          newConfig.panelOverheadColors = [...currentOverheadPanelColors, ...Array(newOverheadParts - currentOverheadPanelColors.length).fill(null)];
        } else {
          newConfig.panelOverheadColors = currentOverheadPanelColors.slice(0, newOverheadParts);
        }
        if (newOverheadParts > currentOverheadPanelLineTypes.length) {
          newConfig.panelOverheadLineTypes = [...currentOverheadPanelLineTypes, ...Array(newOverheadParts - currentOverheadPanelLineTypes.length).fill('none')];
        } else {
          newConfig.panelOverheadLineTypes = currentOverheadPanelLineTypes.slice(0, newOverheadParts);
        }
        const currentOverheadVent = prev.ventilatorOverheadParts || [];
        newConfig.ventilatorOverheadParts = [...currentOverheadVent.slice(0, newOverheadParts), ...Array(Math.max(0, newOverheadParts - currentOverheadVent.length)).fill(false)];
      }
      
      // When overhead changes, reset overhead parts to 1 if switching to no-overhead
      if (field === 'overhead' && value === 'no-overhead') {
        newConfig.overheadParts = 1;
        newConfig.overheadPartWindowTypes = ['fixed-sash'];
        newConfig.overheadPartWidths = [prev.width || 1500];
        newConfig.ventilatorOverheadParts = [false];
      }
      
      // When overhead is first selected, initialize overhead part widths and height
      if (field === 'overhead' && value === 'overhead' && prev.overhead === 'no-overhead') {
        const overheadParts = prev.overheadParts || 1;
        const targetWidth = prev.width || DIMENSION_CONSTRAINTS.MIN_WIDTH;
        const baseWidth = Math.floor(targetWidth / overheadParts);
        const overheadPartWidths = Array(overheadParts).fill(baseWidth);
        // Distribute any remainder to the last part so sum matches total width
        const remainder = targetWidth - baseWidth * overheadParts;
        overheadPartWidths[overheadParts - 1] += remainder;
        newConfig.overheadPartWidths = overheadPartWidths;
        // Initialize overhead height to 20% of current height, clamped to 400-800
        const initialOverheadHeight = Math.round((prev.height || 1500) * 0.20);
        newConfig.overheadHeight = Math.max(400, Math.min(800, initialOverheadHeight));
        const partsCount = prev.parts || 1;
        newConfig.overheadPerPart = Array(partsCount).fill(false);
      }

      // Global vs per-part overhead/underlight are mutually exclusive
      if (field === 'overheadPerPart' && Array.isArray(value) && value.some(Boolean)) {
        newConfig.overhead = 'no-overhead';
      }
      if (field === 'underlightPerPart' && Array.isArray(value) && value.some(Boolean)) {
        newConfig.underlight = 'no-underlight';
      }
      if (field === 'underlight' && value === 'underlight' && prev.underlight === 'no-underlight') {
        const partsCount = prev.parts || 1;
        newConfig.underlightPerPart = Array(partsCount).fill(false);
      }
      
      if (field === 'overheadHeight') {
        const totalHeight = prev.height || DIMENSION_CONSTRAINTS.MIN_HEIGHT;
        const maxHalf = Math.floor(totalHeight / 2);
        const minOverhead = 150;
        const clampedOverheadHeight = Math.max(minOverhead, Math.min(maxHalf, value));
        newConfig.overheadHeight = clampedOverheadHeight;
      }
      
      if (field === 'underlightHeight') {
        const totalHeight = prev.height || DIMENSION_CONSTRAINTS.MIN_HEIGHT;
        const maxHalf = Math.floor(totalHeight / 2);
        const minUnder = 150;
        const clampedUnderlightHeight = Math.max(minUnder, Math.min(maxHalf, value));
        newConfig.underlightHeight = clampedUnderlightHeight;
      }
      
      // When underlight parts change (bottom fixed sections)
      if (field === 'underlightParts') {
        const newUnderlightParts = value;
        newConfig.underlightParts = newUnderlightParts;
        // Adjust underlightPartWindowTypes to match new count
        const currentUnderlightParts = prev.underlightParts || 1;
        const currentTypes = prev.underlightPartWindowTypes || ['fixed-sash'];
        if (newUnderlightParts > currentUnderlightParts) {
          const lastType = currentTypes[currentTypes.length - 1] || 'fixed-sash';
          newConfig.underlightPartWindowTypes = [
            ...currentTypes,
            ...Array(newUnderlightParts - currentUnderlightParts).fill(lastType),
          ];
        } else if (newUnderlightParts < currentUnderlightParts) {
          newConfig.underlightPartWindowTypes = currentTypes.slice(0, newUnderlightParts);
        }
        
        // Initialize underlight panel colors and line types arrays to match new underlight parts count
        const currentUnderlightPanelColors = prev.panelUnderlightColors || [];
        const currentUnderlightPanelLineTypes = prev.panelUnderlightLineTypes || [];
        if (newUnderlightParts > currentUnderlightPanelColors.length) {
          newConfig.panelUnderlightColors = [...currentUnderlightPanelColors, ...Array(newUnderlightParts - currentUnderlightPanelColors.length).fill(null)];
        } else {
          newConfig.panelUnderlightColors = currentUnderlightPanelColors.slice(0, newUnderlightParts);
        }
        if (newUnderlightParts > currentUnderlightPanelLineTypes.length) {
          newConfig.panelUnderlightLineTypes = [...currentUnderlightPanelLineTypes, ...Array(newUnderlightParts - currentUnderlightPanelLineTypes.length).fill('none')];
        } else {
          newConfig.panelUnderlightLineTypes = currentUnderlightPanelLineTypes.slice(0, newUnderlightParts);
        }
        if (!newConfig.underlightPartWindowTypes || newConfig.underlightPartWindowTypes.length !== newUnderlightParts) {
          newConfig.underlightPartWindowTypes = Array(newUnderlightParts).fill('fixed-sash');
        }
        // Adjust underlightPartWidths to match new count (equal widths)
        const totalWidth = prev.width || 1500;
        newConfig.underlightPartWidths = Array(newUnderlightParts).fill(Math.floor(totalWidth / newUnderlightParts));
        const currentUnderlightVent = prev.ventilatorUnderlightParts || [];
        newConfig.ventilatorUnderlightParts = [...currentUnderlightVent.slice(0, newUnderlightParts), ...Array(Math.max(0, newUnderlightParts - currentUnderlightVent.length)).fill(false)];
      }
      
      // When underlight toggles off, reset parts and restore default height
      if (field === 'underlight' && value === 'no-underlight') {
        newConfig.underlightParts = 1;
        newConfig.underlightHeight = 400;
        newConfig.underlightPartWindowTypes = ['fixed-sash'];
        newConfig.underlightPartWidths = [prev.width || 1500];
        newConfig.ventilatorUnderlightParts = [false];
      }
      
      // Reset opening type if raamtype changes (for single part mode)
      if (field === 'raamtype') {
        const validOpenings = openingTypes.filter(ot => ot.validFor.includes(value));
        newConfig.openingType = validOpenings[0]?.id || 'none';
        // Clear hardware and accessories that don't apply
        newConfig.hardware = newConfig.hardware.filter(h => 
          hardware.find(hw => hw.id === h)?.applicableTo.includes(value)
        );
        newConfig.accessories = newConfig.accessories.filter(a => 
          accessories.find(acc => acc.id === a)?.applicableTo.includes(value)
        );
        // When parts === 1, also update partWindowTypes[0] to keep them in sync
        if (prev.parts === 1) {
          newConfig.partWindowTypes = [value];
        }
      }
      
      return newConfig;
    });
  };
  
  // Handle window type change for a specific part
  const handlePartWindowTypeChange = (partIndex, windowTypeId) => {
    setConfig(prev => {
      const parts = prev.parts || 1;
      // Ensure the array has the correct length, filling with 'fixed-sash' if needed
      const currentTypes = prev.partWindowTypes || [];
      const newPartWindowTypes = Array(parts)
        .fill(null)
        .map((_, idx) => currentTypes[idx] || 'fixed-sash');
      newPartWindowTypes[partIndex] = windowTypeId;
      // Keep raamtype in sync for single-part drawings (options panel + edit reload)
      return {
        ...prev,
        partWindowTypes: newPartWindowTypes,
        ...(parts === 1 ? { raamtype: windowTypeId } : {}),
      };
    });
  };
  
  // Handle window type change for a specific overhead part
  const handleOverheadPartWindowTypeChange = (partIndex, windowTypeId) => {
    setConfig(prev => {
      const newOverheadPartWindowTypes = [...(prev.overheadPartWindowTypes || [])];
      newOverheadPartWindowTypes[partIndex] = windowTypeId;
      return { ...prev, overheadPartWindowTypes: newOverheadPartWindowTypes };
    });
  };

  // Handle window type change for a specific underlight part
  const handleUnderlightPartWindowTypeChange = (partIndex, windowTypeId) => {
    setConfig(prev => {
      const underParts = prev.underlightParts || 1;
      const currentTypes = prev.underlightPartWindowTypes || [];
      const newUnderlightPartWindowTypes = Array(underParts)
        .fill(null)
        .map((_, idx) => currentTypes[idx] || 'fixed-sash');
      newUnderlightPartWindowTypes[partIndex] = windowTypeId;
      return { ...prev, underlightPartWindowTypes: newUnderlightPartWindowTypes };
    });
  };

  // Handle window type change for per-part underlight (from part settings)
  const handlePartUnderlightWindowTypeChange = (partIndex, windowTypeId) => {
    setConfig(prev => {
      const partsCount = prev.parts || 1;
      const currentTypes = prev.partUnderlightWindowTypes || [];
      const newPartUnderlightWindowTypes = Array(partsCount)
        .fill(null)
        .map((_, idx) => currentTypes[idx] || 'fixed-sash');
      newPartUnderlightWindowTypes[partIndex] = windowTypeId;
      return applyPartBandSectionWindowType(
        { ...prev, partUnderlightWindowTypes: newPartUnderlightWindowTypes },
        'underlight',
        partIndex,
        0,
        windowTypeId
      );
    });
  };

  // Handle window type change for per-part overhead (from part settings)
  const handlePartOverheadWindowTypeChange = (partIndex, windowTypeId) => {
    setConfig(prev => {
      const partsCount = prev.parts || 1;
      const currentTypes = prev.partOverheadWindowTypes || [];
      const newPartOverheadWindowTypes = Array(partsCount)
        .fill(null)
        .map((_, idx) => currentTypes[idx] || 'fixed-sash');
      newPartOverheadWindowTypes[partIndex] = windowTypeId;
      return applyPartBandSectionWindowType(
        { ...prev, partOverheadWindowTypes: newPartOverheadWindowTypes },
        'overhead',
        partIndex,
        0,
        windowTypeId
      );
    });
  };

  const getPartWidthMm = (cfg, partIndex) => {
    const partsCount = cfg.parts || 1;
    const totalWidth = cfg.width || 0;
    if (!totalWidth) return 0;
    const widths = ensurePartWidthsSum(cfg.partWidths, partsCount, totalWidth);
    return widths[partIndex] ?? totalWidth / partsCount;
  };

  const handlePartOverheadSectionCountChange = (partIndex, count) => {
    setConfig((prev) =>
      applyPartBandSectionCount(prev, 'overhead', partIndex, count, getPartWidthMm(prev, partIndex))
    );
  };

  const handlePartUnderlightSectionCountChange = (partIndex, count) => {
    setConfig((prev) =>
      applyPartBandSectionCount(prev, 'underlight', partIndex, count, getPartWidthMm(prev, partIndex))
    );
  };

  const handlePartOverheadSectionWindowTypeChange = (partIndex, sectionIndex, windowTypeId) => {
    setConfig((prev) =>
      applyPartBandSectionWindowType(prev, 'overhead', partIndex, sectionIndex, windowTypeId)
    );
  };

  const handlePartUnderlightSectionWindowTypeChange = (partIndex, sectionIndex, windowTypeId) => {
    setConfig((prev) =>
      applyPartBandSectionWindowType(prev, 'underlight', partIndex, sectionIndex, windowTypeId)
    );
  };

  const handlePartOverheadSectionWidthChange = (partIndex, sectionIndex, widthMm) => {
    setConfig((prev) =>
      applyPartBandSectionWidth(
        prev,
        'overhead',
        partIndex,
        sectionIndex,
        widthMm,
        getPartWidthMm(prev, partIndex)
      )
    );
  };

  const handlePartUnderlightSectionWidthChange = (partIndex, sectionIndex, widthMm) => {
    setConfig((prev) =>
      applyPartBandSectionWidth(
        prev,
        'underlight',
        partIndex,
        sectionIndex,
        widthMm,
        getPartWidthMm(prev, partIndex)
      )
    );
  };

  // Handle per-part width change from the drawing board
  const handlePartWidthChange = (partIndex, newWidthMm) => {
    setConfig(prev => {
      const parts = prev.parts || 1;
      const totalWidth = prev.width || 0;
      if (parts <= 1 || !totalWidth) return prev;

      const currentWidths = ensurePartWidthsSum(prev.partWidths, parts, totalWidth);
      const nextWidths = applyPartWidthChange(
        currentWidths,
        partIndex,
        newWidthMm,
        totalWidth,
        DIMENSION_CONSTRAINTS.MIN_PART_WIDTH ?? MIN_PART_WIDTH_MM
      );

      return {
        ...prev,
        partWidths: nextWidths,
        widthManuallySet: true,
      };
    });
  };
  
  // Handle per-overhead-part width change from the drawing board
  const handleOverheadPartWidthChange = (partIndex, newWidthMm) => {
    setConfig(prev => {
      const overheadParts = prev.overheadParts || 1;
      const totalWidth = prev.width || 0;
      if (overheadParts <= 1 || !totalWidth) return prev;

      const currentWidths = ensurePartWidthsSum(prev.overheadPartWidths, overheadParts, totalWidth);
      const nextWidths = applyPartWidthChange(
        currentWidths,
        partIndex,
        newWidthMm,
        totalWidth,
        DIMENSION_CONSTRAINTS.MIN_PART_WIDTH ?? MIN_PART_WIDTH_MM
      );

      return {
        ...prev,
        overheadPartWidths: nextWidths,
        widthManuallySet: true,
      };
    });
  };
  
  // Handle per-underlight-part width change from the drawing board
  const handleUnderlightPartWidthChange = (partIndex, newWidthMm) => {
    setConfig(prev => {
      const underlightParts = prev.underlightParts || 1;
      const totalWidth = prev.width || 0;
      if (underlightParts <= 1 || !totalWidth) return prev;

      const currentWidths = ensurePartWidthsSum(prev.underlightPartWidths, underlightParts, totalWidth);
      const nextWidths = applyPartWidthChange(
        currentWidths,
        partIndex,
        newWidthMm,
        totalWidth,
        DIMENSION_CONSTRAINTS.MIN_PART_WIDTH ?? MIN_PART_WIDTH_MM
      );

      return {
        ...prev,
        underlightPartWidths: nextWidths,
        widthManuallySet: true,
      };
    });
  };
  
  // When parts === 1, prefer partWindowTypes[0] (board selection) over stale raamtype.
  // Previously this overwrote partWindowTypes from raamtype, which wiped tilt/turn on Edit drawing.
  useEffect(() => {
    if (config.parts !== 1) return;
    const fromParts = config.partWindowTypes?.[0];
    if (!fromParts || fromParts === config.raamtype) return;
    setConfig((prev) => ({
      ...prev,
      raamtype: prev.partWindowTypes?.[0] || prev.raamtype,
    }));
  }, [config.parts, config.partWindowTypes, config.raamtype]);
  
  const selectedRaamtype = config.parts === 1
    ? raamtypes.find(r => r.id === (config.partWindowTypes?.[0] || config.raamtype))
    : raamtypes.find(r => r.id === (config.partWindowTypes?.[0] || 'fixed-glass'));
  // Use getColorById to handle both regular colors and RAL colors
  const selectedColorData = getColorById(config.color);
  const selectedColor = selectedColorData || colors.find(c => c.id === config.color) || { id: 'anthracite', name: 'Anthracite Grey', hex: '#3D3D3D' };
  const selectedInteriorFrameColorData = getColorById(config.interiorFrameColor);
  const selectedInteriorFrameColor = selectedInteriorFrameColorData || colors.find(c => c.id === config.interiorFrameColor) || { id: DEFAULT_INTERIOR_WHITE_ID, name: 'White Smooth', hex: '#F6F6F6' };
  const selectedGlassTexture = glassTextures.find(g => g.id === config.glassTexture);
  const selectedGlazingType = glazingTypes.find(g => g.id === config.glazingType);

  const handleReset = () => {
    const nextConfig = buildDefaultConfig();
    setConfig(nextConfig);
    setCurrentQuantityInput(1);
    resetBaseline(nextConfig);
  };

  const handleAdditionalItemsChange = (updater) => {
    markDirty();
    setConfig((prev) => applyAdditionalItemsChange(prev, updater));
  };

  const kozijnenPreview = Kozijnen ? (
    <Kozijnen
      width={config.width}
      height={config.height}
      windowType={selectedRaamtype}
      color={selectedColor}
      interiorFrameColor={selectedInteriorFrameColor}
      glassTexture={selectedGlassTexture}
      glazingType={selectedGlazingType}
      parts={config.parts}
      partWindowTypes={config.partWindowTypes}
      woodJoint={config.woodJoint}
      overhead={config.overhead}
      overheadParts={config.overheadParts}
      overheadHeight={config.overheadHeight}
      overheadPartWindowTypes={config.overheadPartWindowTypes}
      overheadPartWidths={config.overheadPartWidths}
      underlight={config.underlight}
      underlightParts={config.underlightParts}
      underlightHeight={config.underlightHeight}
      underlightPartWindowTypes={config.underlightPartWindowTypes}
      underlightPartWidths={config.underlightPartWidths}
      raamtypes={raamtypes}
      onPartWindowTypeChange={handlePartWindowTypeChange}
      onOverheadPartWindowTypeChange={handleOverheadPartWindowTypeChange}
      onUnderlightPartWindowTypeChange={handleUnderlightPartWindowTypeChange}
      partWidths={config.partWidths}
      onPartWidthChange={handlePartWidthChange}
      onOverheadPartWidthChange={handleOverheadPartWidthChange}
      onUnderlightPartWidthChange={handleUnderlightPartWidthChange}
      onOverheadHeightChange={(newOverheadHeight) => handleConfigChange('overheadHeight', newOverheadHeight)}
      onUnderlightHeightChange={(newUnderlightHeight) => handleConfigChange('underlightHeight', newUnderlightHeight)}
      onWidthChange={(newWidth) => handleConfigChange('width', newWidth)}
      onHeightChange={(newHeight) => handleConfigChange('height', newHeight)}
      panelColors={config.panelColors}
      panelOverheadColors={config.panelOverheadColors}
      panelUnderlightColors={config.panelUnderlightColors}
      panelLineTypes={config.panelLineTypes}
      panelOverheadLineTypes={config.panelOverheadLineTypes}
      panelUnderlightLineTypes={config.panelUnderlightLineTypes}
      onPanelColorChange={(index, colorId) => {
        setConfig((prev) => {
          const newPanelColors = [...(prev.panelColors || [])];
          newPanelColors[index] = colorId;
          return { ...prev, panelColors: newPanelColors };
        });
      }}
      onPanelOverheadColorChange={(index, colorId) => {
        setConfig((prev) => {
          const newPanelColors = [...(prev.panelOverheadColors || [])];
          newPanelColors[index] = colorId;
          return { ...prev, panelOverheadColors: newPanelColors };
        });
      }}
      onPanelUnderlightColorChange={(index, colorId) => {
        setConfig((prev) => {
          const newPanelColors = [...(prev.panelUnderlightColors || [])];
          newPanelColors[index] = colorId;
          return { ...prev, panelUnderlightColors: newPanelColors };
        });
      }}
      onPanelLineTypeChange={(index, lineType) => {
        setConfig((prev) => {
          const newPanelLineTypes = [...(prev.panelLineTypes || [])];
          newPanelLineTypes[index] = lineType;
          return { ...prev, panelLineTypes: newPanelLineTypes };
        });
      }}
      onPanelOverheadLineTypeChange={(index, lineType) => {
        setConfig((prev) => {
          const newPanelLineTypes = [...(prev.panelOverheadLineTypes || [])];
          newPanelLineTypes[index] = lineType;
          return { ...prev, panelOverheadLineTypes: newPanelLineTypes };
        });
      }}
      onPanelUnderlightLineTypeChange={(index, lineType) => {
        setConfig((prev) => {
          const newPanelLineTypes = [...(prev.panelUnderlightLineTypes || [])];
          newPanelLineTypes[index] = lineType;
          return { ...prev, panelUnderlightLineTypes: newPanelLineTypes };
        });
      }}
      rampDorpel={config.rampDorpel}
      onRampDorpelChange={(value) => handleConfigChange('rampDorpel', value)}
      ventilatorParts={config.ventilatorParts}
      ventilatorOverheadParts={config.ventilatorOverheadParts}
      ventilatorUnderlightParts={config.ventilatorUnderlightParts}
      onVentilatorPartsChange={(arr) => handleConfigChange('ventilatorParts', arr)}
      onVentilatorOverheadPartsChange={(arr) => handleConfigChange('ventilatorOverheadParts', arr)}
      onVentilatorUnderlightPartsChange={(arr) => handleConfigChange('ventilatorUnderlightParts', arr)}
      overheadPerPart={config.overheadPerPart}
      onOverheadPerPartChange={(arr) => handleConfigChange('overheadPerPart', arr)}
      underlightPerPart={config.underlightPerPart}
      onUnderlightPerPartChange={(arr) => handleConfigChange('underlightPerPart', arr)}
      partUnderlightWindowTypes={config.partUnderlightWindowTypes}
      onPartUnderlightWindowTypeChange={handlePartUnderlightWindowTypeChange}
      partOverheadWindowTypes={config.partOverheadWindowTypes}
      onPartOverheadWindowTypeChange={handlePartOverheadWindowTypeChange}
      partOverheadSections={config.partOverheadSections}
      partUnderlightSections={config.partUnderlightSections}
      partOverheadSectionWindowTypes={config.partOverheadSectionWindowTypes}
      partUnderlightSectionWindowTypes={config.partUnderlightSectionWindowTypes}
      partOverheadSectionWidths={config.partOverheadSectionWidths}
      partUnderlightSectionWidths={config.partUnderlightSectionWidths}
      onPartOverheadSectionCountChange={handlePartOverheadSectionCountChange}
      onPartUnderlightSectionCountChange={handlePartUnderlightSectionCountChange}
      onPartOverheadSectionWindowTypeChange={handlePartOverheadSectionWindowTypeChange}
      onPartUnderlightSectionWindowTypeChange={handlePartUnderlightSectionWindowTypeChange}
      onPartOverheadSectionWidthChange={handlePartOverheadSectionWidthChange}
      onPartUnderlightSectionWidthChange={handlePartUnderlightSectionWidthChange}
      screenElectricallyOperated={config.screenElectricallyOperated}
      rollerShutterElectricallyOperated={config.rollerShutterElectricallyOperated}
      screenColor={config.screenColor}
      rollerShutterColor={config.rollerShutterColor}
    />
  ) : (
    <div className="flex items-center justify-center h-full text-red-600">
      Window component failed to load. Check the browser console for errors.
    </div>
  );

  return (
    <ConfiguratorShell
      title={t('configurator.windowConfigurator')}
      drawingBoardRef={drawingBoardRef}
      onReset={handleReset}
      isDirty={isDirty}
      onMarkClean={markClean}
      saveCurrentRef={saveCurrentRef}
      optionsPanel={
        <OptionsPanel
          config={config}
          onConfigChange={handleConfigChange}
          windowTypes={windowTypes}
          profileSystems={profileSystems}
          glassTextures={glassTextures}
          glazingTypes={glazingTypes}
          colors={colors}
          openingTypes={openingTypes}
          hardware={hardware}
          accessories={accessories}
          dimensionConstraints={DIMENSION_CONSTRAINTS}
          partsOptions={partsOptions}
          overheadOptions={overheadOptions}
          woodJointOptions={woodJointOptions}
          divisionTypes={divisionTypes}
          raamtypes={raamtypes}
          additionalItems={config.additionalItems || createDefaultAdditionalItems()}
          onAdditionalItemsChange={handleAdditionalItemsChange}
        />
      }
      previewPanel={kozijnenPreview}
      summaryPanel={
        <Summary
          config={config}
          pricing={PRICING}
          profileSystems={profileSystems}
          glassTextures={glassTextures}
          glazingTypes={glazingTypes}
          colors={colors}
          raamtypes={raamtypes}
          hardware={hardware}
          accessories={accessories}
          woodJointOptions={woodJointOptions}
          partsOptions={partsOptions}
          overheadOptions={overheadOptions}
          divisionTypes={divisionTypes}
          drawingBoardRef={drawingBoardRef}
          pageType="kozijnen"
          onConfigChange={handleConfigChange}
          onQuantityInputChange={setCurrentQuantityInput}
          saveCurrentRef={saveCurrentRef}
          onSaved={markClean}
        />
      }
    />
  );
};

export default Configurator;
